/**
 * Trade Communications view: sidebar + content (email client layout)
 * Fetches email activities, limbo messages, and email accounts; composes sidebar and content.
 * Syncs folder and search (q) to URL so state survives refresh.
 */
import { useMemo, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import { useActivities } from "@/hooks/api/use-activities";
import { useEmailAccounts, useEmailCounts } from "@/hooks/api/use-email-accounts";
import { useLimboMessages } from "@/hooks/api/use-email-limbo";
import { mapActivitiesToFrontend } from "@/utils/activity-mapper";
import { TradeCommsSidebar } from "./trade-comms-sidebar";
import { TradeCommsContent } from "./trade-comms-content";
import { folderToParam, paramToFolder, type TradeCommsFolder } from "./types";
import type { ActivityAudience } from "@/types/activity";

// Debounce hook
function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === "true";

export interface TradeCommsViewProps {
    audience: ActivityAudience;
    onMarkActivityAsRead?: (activityId: string) => void;
    className?: string;
}

export function TradeCommsView({ audience, onMarkActivityAsRead, className }: TradeCommsViewProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [folder, setFolder] = useState<TradeCommsFolder>({ type: "inbox" });
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Search state: init from URL so refresh restores query
    const [searchInput, setSearchInput] = useState(() => searchParams.get("q") ?? "");
    const initialMessageId = searchParams.get("messageId");
    const searchKeyword = useDebouncedValue(searchInput, 500);
    
    // Infinite scroll state for inbox
    const [inboxSkip, setInboxSkip] = useState(0);
    const [inboxHasMore, setInboxHasMore] = useState(true);
    const [allInboxActivities, setAllInboxActivities] = useState<any[]>([]);
    
    // Infinite scroll state for limbo
    const [limboSkip, setLimboSkip] = useState(0);
    const [limboHasMore, setLimboHasMore] = useState(true);
    const [allLimboMessages, setAllLimboMessages] = useState<any[]>([]);
    
    // Reset pagination when search changes
    useEffect(() => {
        setInboxSkip(0);
        setLimboSkip(0);
        setAllInboxActivities([]);
        setAllLimboMessages([]);
        setInboxHasMore(true);
        setLimboHasMore(true);
    }, [searchKeyword]);

    const { data: apiResponse, isLoading: isLoadingInbox } = useActivities(
        audience,
        {
            activity_category: "email",
            skip: inboxSkip,
            limit: 50,
            keyword: searchKeyword || undefined,
        },
        { enabled: !USE_MOCK_DATA, refetchInterval: 30000 }
    );

    const { data: accounts = [] } = useEmailAccounts(undefined);

    // Sync folder from URL (e.g. initial load with ?folder=limbo or browser back)
    useEffect(() => {
        const folderParam = searchParams.get("folder");
        if (folderParam && folderToParam(folder) !== folderParam) {
            setFolder(paramToFolder(folderParam, accounts));
        }
    }, [searchParams, accounts, folder]);

    // Push debounced search to URL
    useEffect(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (searchKeyword) next.set("q", searchKeyword);
                else next.delete("q");
                return next;
            },
            { replace: true }
        );
    }, [searchKeyword]);

    const { data: limboData, isLoading: isLoadingLimbo } = useLimboMessages(
        limboSkip,
        50,
        undefined, // accountId
        searchKeyword || undefined, // keyword
        {
            enabled: true,
            refetchInterval: 30000,
        }
    );

    // Fetch real email counts from backend (not limited by pagination)
    const { data: emailCounts } = useEmailCounts({
        enabled: true,
        refetchInterval: 60000,
    });
    
    // Merge inbox results for infinite scroll
    useEffect(() => {
        if (apiResponse?.activities) {
            setAllInboxActivities(prev =>
                inboxSkip === 0 ? apiResponse.activities : [...prev, ...apiResponse.activities]
            );
            setInboxHasMore(apiResponse.activities.length === 50);
        }
    }, [apiResponse, inboxSkip]);
    
    // Merge limbo results for infinite scroll
    useEffect(() => {
        if (limboData) {
            setAllLimboMessages(prev =>
                limboSkip === 0 ? limboData : [...prev, ...limboData]
            );
            setLimboHasMore(limboData.length === 50);
        }
    }, [limboData, limboSkip]);
    
    // Handlers for infinite scroll
    const handleLoadMoreInbox = useCallback(() => {
        if (inboxHasMore && !isLoadingInbox) {
            setInboxSkip(prev => prev + 50);
        }
    }, [inboxHasMore, isLoadingInbox]);
    
    const handleLoadMoreLimbo = useCallback(() => {
        if (limboHasMore && !isLoadingLimbo) {
            setLimboSkip(prev => prev + 50);
        }
    }, [limboHasMore, isLoadingLimbo]);

    const emailActivities = useMemo(() => {
        if (USE_MOCK_DATA || allInboxActivities.length === 0) return [];
        return mapActivitiesToFrontend(allInboxActivities);
    }, [allInboxActivities]);

    // Extract counts from API response (real database counts, not limited by pagination)
    const limboCountAll = emailCounts?.limbo_total ?? 0;
    const inboxUnreadAll = emailCounts?.inbox_unread_total ?? 0;

    // Create maps for quick access by account_id
    const limboCountByAccount = useMemo(() => {
        const map: Record<string, number> = {};
        emailCounts?.by_account.forEach((acc) => {
            map[acc.account_id] = acc.limbo;
        });
        return map;
    }, [emailCounts]);

    const inboxUnreadByAccount = useMemo(() => {
        const map: Record<string, number> = {};
        emailCounts?.by_account.forEach((acc) => {
            map[acc.account_id] = acc.inbox_unread;
        });
        return map;
    }, [emailCounts]);

    const handleFolderChange = useCallback(
        (f: TradeCommsFolder) => {
            setFolder(f);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("folder", folderToParam(f));
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    return (
        <div className={className ?? "flex min-h-[480px] rounded-xl border border-secondary bg-primary shadow-xs overflow-hidden"}>
            <TradeCommsSidebar
                folder={folder}
                onFolderChange={handleFolderChange}
                accounts={accounts}
                limboCountAll={limboCountAll}
                limboCountByAccount={limboCountByAccount}
                inboxUnreadAll={inboxUnreadAll}
                inboxUnreadByAccount={inboxUnreadByAccount}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
            />
            <TradeCommsContent
                folder={folder}
                emailActivities={emailActivities}
                limboMessages={allLimboMessages}
                onMarkActivityAsRead={onMarkActivityAsRead}
                threadByContainer={true}
                searchInput={searchInput}
                onSearchChange={setSearchInput}
                onLoadMoreInbox={handleLoadMoreInbox}
                onLoadMoreLimbo={handleLoadMoreLimbo}
                hasMoreInbox={inboxHasMore}
                hasMoreLimbo={limboHasMore}
                isLoadingInbox={isLoadingInbox}
                isLoadingLimbo={isLoadingLimbo}
                initialMessageId={initialMessageId}
            />
        </div>
    );
}
